import React, { useState } from 'react';
import { Card, Button, Modal, Input, message, Divider, Tag, Space, Typography } from 'antd';
import { EyeOutlined, DownloadOutlined, UploadOutlined, CopyOutlined } from '@ant-design/icons';
import { RulingConfigUtils } from '../../utils/rulingConfigUtils';

const { TextArea } = Input;
const { Text, Title } = Typography;

const ConfigurationPreview = ({ 
  configuration, 
  onImport,
  title = "Configuration Preview"
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [importText, setImportText] = useState('');

  // Debug logging to understand the data structure (temporary)
  console.log('ConfigurationPreview received configuration:', configuration);

  const handleExport = () => {
    const configJson = JSON.stringify(configuration, null, 2);
    
    // Create and download file
    const blob = new Blob([configJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${configuration.name || 'configuration'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    message.success('Configuration exported successfully!');
  };

  const handleCopyToClipboard = () => {
    const configJson = JSON.stringify(configuration, null, 2);
    navigator.clipboard.writeText(configJson).then(() => {
      message.success('Configuration copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  const handleImport = () => {
    try {
      const importedConfig = JSON.parse(importText);
      
      // Validate the imported configuration
      const validationErrors = RulingConfigUtils.validateConfiguration(importedConfig);
      if (validationErrors.length > 0) {
        message.error(`Invalid configuration: ${validationErrors[0]}`);
        return;
      }
      
      onImport(importedConfig);
      setIsImportModalVisible(false);
      setImportText('');
      message.success('Configuration imported successfully!');
    } catch (error) {
      message.error('Invalid JSON format');
    }
  };

  const renderConditionSummary = (condition, index) => {
    if (condition.group) {
      return (
        <div key={index} style={{ 
          marginLeft: 16, 
          padding: 8, 
          border: '1px dashed #444444',
          borderRadius: 4,
          backgroundColor: '#1a1a1b'
        }}>
          <Text strong style={{ color: '#ffffff' }}>Group ({condition.group_logic}):</Text>
          {condition.group.map((groupCondition, groupIndex) => (
            <div key={groupIndex} style={{ marginLeft: 16, marginTop: 4 }}>
              {renderSingleCondition(groupCondition, groupIndex)}
            </div>
          ))}
        </div>
      );
    }
    
    return renderSingleCondition(condition, index);
  };

  const renderSingleCondition = (condition, index) => {
    let valueDisplay = condition.value;
    
    if (condition.operator === 'between' || condition.operator === 'not_between') {
      valueDisplay = `${condition.min} - ${condition.max}`;
    } else if (Array.isArray(condition.value)) {
      valueDisplay = `[${condition.value.join(', ')}]`;
    }

    return (
      <div key={index} style={{ marginBottom: 4 }}>
        <Space wrap>
          <Tag color="blue">{condition.scorecard}</Tag>
          <Text code style={{ color: '#ffffff', backgroundColor: '#262626', padding: '2px 6px', borderRadius: '4px' }}>
            {RulingConfigUtils.getOperatorLabel(condition.operator)}
          </Text>
          <Tag color={condition.type === 'numeric' ? 'green' : 'purple'}>
            {valueDisplay}
          </Tag>
        </Space>
      </div>
    );
  };

  const renderRuleSet = (ruleSet, ruleName) => {
    if (!ruleSet || !ruleSet.conditions) return null;

    return (
      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ 
          color: ruleName === 'pass' ? '#52c41a' : 
                 ruleName === 'refer' ? '#faad14' : 
                 ruleName === 'fail' ? '#ff4d4f' : '#1890ff'
        }}>
          {ruleName.toUpperCase()} Rules ({ruleSet.logic})
        </Title>
        
        {ruleSet.conditions.map((condition, index) => (
          <div key={index}>
            {renderConditionSummary(condition, index)}
            {index < ruleSet.conditions.length - 1 && (
              <Divider style={{ margin: '8px 0' }}>
                <Text type="secondary" style={{ fontSize: '10px' }}>
                  {ruleSet.logic}
                </Text>
              </Divider>
            )}
          </div>
        ))}
      </div>
    );
  };

  const validationErrors = configuration 
    ? RulingConfigUtils.validateConfiguration(configuration) 
    : ['Configuration data not available'];

  if (!configuration) {
    return (
      <Card title={title} size="small">
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          Loading configuration...
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={title}
        size="small"
        extra={
          <Space>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setIsModalVisible(true)}
              size="small"
            >
              Preview
            </Button>
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={handleCopyToClipboard}
              size="small"
            >
              Copy
            </Button>
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              size="small"
            >
              Export
            </Button>
            {onImport && (
              <Button
                type="text"
                icon={<UploadOutlined />}
                onClick={() => setIsImportModalVisible(true)}
                size="small"
              >
                Import
              </Button>
            )}
          </Space>
        }
      >
        <div style={{ fontSize: '13px', color: '#ffffff' }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ color: '#ffffff' }}>Name:</Text> <Text style={{ color: '#b3b3b3' }}>{configuration.name}</Text>
          </div>
          
          {configuration.description && (
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ color: '#ffffff' }}>Description:</Text> <Text style={{ color: '#b3b3b3' }}>{configuration.description}</Text>
            </div>
          )}
          
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ color: '#ffffff' }}>Version:</Text> <Text style={{ color: '#b3b3b3' }}>{configuration.version}</Text>
          </div>
          
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ color: '#ffffff' }}>Evaluation Order:</Text>{' '}
            <Space>
              {(configuration.evaluation_order || []).map(rule => (
                <Tag key={rule} size="small">{rule}</Tag>
              ))}
            </Space>
          </div>
          
          {validationErrors.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text type="danger" strong>Validation Errors:</Text>
              <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>
                    <Text type="danger" style={{ fontSize: '12px' }}>{error}</Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>

      {/* Preview Modal */}
      <Modal
        title="Configuration Details"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          <Title level={4} style={{ color: '#ffffff' }}>{configuration.name}</Title>
          
          {configuration.description && (
            <div style={{ marginBottom: 16 }}>
              <Text style={{ color: '#b3b3b3' }}>{configuration.description}</Text>
            </div>
          )}
          
          <Divider />
          
          {configuration.rules && Object.entries(configuration.rules).map(([ruleName, ruleSet]) => (
            <div key={ruleName}>
              {renderRuleSet(ruleSet, ruleName)}
            </div>
          ))}
          
          <Divider />
          
          <div>
            <Text strong style={{ color: '#ffffff' }}>Evaluation Order:</Text>{' '}
            <Text style={{ color: '#b3b3b3' }}>{(configuration.evaluation_order || []).join(' → ')}</Text>
          </div>
          
          <div style={{ marginTop: 8 }}>
            <Text strong style={{ color: '#ffffff' }}>Default Outcome:</Text> <Text style={{ color: '#b3b3b3' }}>{configuration.default_outcome}</Text>
          </div>
        </div>
      </Modal>

      {/* Import Modal */}
      <Modal
        title="Import Configuration"
        open={isImportModalVisible}
        onOk={handleImport}
        onCancel={() => {
          setIsImportModalVisible(false);
          setImportText('');
        }}
        okText="Import"
      >
        <div style={{ marginBottom: 16 }}>
          <Text>Paste the configuration JSON below:</Text>
        </div>
        <TextArea
          rows={10}
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          placeholder="Paste configuration JSON here..."
        />
      </Modal>
    </>
  );
};

export default ConfigurationPreview;